import { useState } from 'react'
import { filterApi } from '../api/search'

interface Filter {
  id: string
  param: string
  condition: string
  value: any
  paramName: string
  conditionName: string
  valueDisplay: string
}

interface FilterPanelProps {
  searchId: string
  articles: any[]
  onFiltersChange: (filters: Filter[]) => void
  onFilteredPmids: (pmids: string[]) => void
  availableTopics?: { id: number; name?: string; count?: number }[]
  topicMapping?: Record<string, number>
}

const PARAM_OPTIONS = [
  { value: 'year', label: 'Year', type: 'number' },
  { value: 'journal', label: 'Journal', type: 'text' },
  { value: 'quartile', label: 'Quartile', type: 'quartile' },
  { value: 'sjr_score', label: 'SJR Score', type: 'number' },
  { value: 'h_index', label: 'H-Index', type: 'number' },
  { value: 'authors', label: 'Authors', type: 'text' },
  { value: 'title', label: 'Title', type: 'text' },
  { value: 'abstract', label: 'Abstract', type: 'text' },
  { value: 'mesh_terms', label: 'MeSH Terms', type: 'array' },
  { value: 'keywords', label: 'Keywords', type: 'text' },
  { value: 'doi', label: 'DOI', type: 'text' },
  { value: 'topic', label: 'Topic', type: 'topic' },
]

const CONDITIONS: Record<string, { value: string; label: string }[]> = {
  number: [
    { value: 'range', label: 'Between' },
    { value: 'min', label: 'At least' },
    { value: 'max', label: 'At most' },
    { value: 'equals', label: 'Equals' },
  ],
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'regex', label: 'Matches regex' },
  ],
  array: [
    { value: 'in', label: 'Includes' },
    { value: 'not_in', label: 'Excludes' },
    { value: 'contains', label: 'Contains' },
  ],
  quartile: [
    { value: 'in', label: 'Is one of' },
    { value: 'not_in', label: 'Is not' },
  ],
  topic: [
    { value: 'in', label: 'Include' },
    { value: 'not_in', label: 'Exclude' },
  ],
}

let filterCounter = 0

export default function FilterPanel({ searchId, articles, onFiltersChange, onFilteredPmids, availableTopics, topicMapping }: FilterPanelProps) {
  const [filters, setFilters] = useState<Filter[]>([])
  const [selectedParam, setSelectedParam] = useState('')
  const [selectedCondition, setSelectedCondition] = useState('')
  const [filterValue, setFilterValue] = useState('')
  const [filterValueMin, setFilterValueMin] = useState('')
  const [filterValueMax, setFilterValueMax] = useState('')
  const [selectedQuartiles, setSelectedQuartiles] = useState<string[]>([])
  const [selectedTopics, setSelectedTopics] = useState<number[]>([])
  const [isExpanded, setIsExpanded] = useState(false)

  const currentParam = PARAM_OPTIONS.find(p => p.value === selectedParam)
  const currentConditions = currentParam ? CONDITIONS[currentParam.type] || [] : []

  const applyFilters = async (currentFilters: Filter[]) => {
    if (currentFilters.length === 0) {
      onFilteredPmids(articles.map(a => a.pmid))
      return
    }

    // Separate topic filters (client-side) from regular filters (backend)
    const topicFilters = currentFilters.filter(f => f.param === 'topic')
    const regularFilters = currentFilters.filter(f => f.param !== 'topic')

    // Start with all articles
    let candidatePmids = articles.map(a => a.pmid)

    // Apply topic filters client-side
    if (topicFilters.length > 0 && topicMapping) {
      for (const topicFilter of topicFilters) {
        const selectedTopics = topicFilter.value as number[]
        if (topicFilter.condition === 'in') {
          // Include: keep only PMIDs that match selected topics
          candidatePmids = candidatePmids.filter(pmid => {
            const articleTopic = topicMapping[pmid]
            return articleTopic !== undefined && selectedTopics.includes(articleTopic)
          })
        } else if (topicFilter.condition === 'not_in') {
          // Exclude: remove PMIDs that match selected topics
          candidatePmids = candidatePmids.filter(pmid => {
            const articleTopic = topicMapping[pmid]
            return articleTopic === undefined || !selectedTopics.includes(articleTopic)
          })
        }
      }
    }

    // If no regular filters, return topic-filtered results
    if (regularFilters.length === 0) {
      onFilteredPmids(candidatePmids)
      return
    }

    // Apply regular filters via backend, but only on candidate PMIDs
    try {
      const filterConditions = regularFilters.map(f => ({
        param: f.param,
        condition: f.condition,
        value: f.value,
      }))

      const result = await filterApi.applyFilters(searchId, filterConditions)
      // Intersect backend results with topic-filtered candidates
      const candidateSet = new Set(candidatePmids)
      const finalPmids = result.pmids.filter((pmid: string) => candidateSet.has(pmid))
      onFilteredPmids(finalPmids)
    } catch (error) {
      console.error('Filter error:', error)
      onFilteredPmids(candidatePmids)
    }
  }

  const addFilter = () => {
    if (!selectedParam || !selectedCondition) return

    let value: any = filterValue
    let valueDisplay = filterValue

    if (selectedCondition === 'range') {
      value = [Number(filterValueMin), Number(filterValueMax)]
      valueDisplay = `${filterValueMin} and ${filterValueMax}`
    } else if (selectedParam === 'quartile') {
      value = selectedQuartiles
      valueDisplay = selectedQuartiles.join(', ')
    } else if (selectedParam === 'topic') {
      value = selectedTopics
      valueDisplay = selectedTopics.map(t => {
        const topic = availableTopics?.find(at => at.id === t)
        return topic?.name || `Topic ${t + 1}`
      }).join(', ')
    }

    const paramInfo = PARAM_OPTIONS.find(p => p.value === selectedParam)
    const conditionInfo = currentConditions.find(c => c.value === selectedCondition)

    filterCounter++
    const newFilter: Filter = {
      id: `filter_${filterCounter}`,
      param: selectedParam,
      condition: selectedCondition,
      value,
      paramName: paramInfo?.label || selectedParam,
      conditionName: conditionInfo?.label || selectedCondition,
      valueDisplay,
    }

    const updatedFilters = [...filters, newFilter]
    setFilters(updatedFilters)
    onFiltersChange(updatedFilters)
    applyFilters(updatedFilters)

    // Reset form
    setSelectedParam('')
    setSelectedCondition('')
    setFilterValue('')
    setFilterValueMin('')
    setFilterValueMax('')
    setSelectedQuartiles([])
    setSelectedTopics([])
  }

  const removeFilter = (filterId: string) => {
    const updatedFilters = filters.filter(f => f.id !== filterId)
    setFilters(updatedFilters)
    onFiltersChange(updatedFilters)
    applyFilters(updatedFilters)
  }

  const clearFilters = () => {
    setFilters([])
    onFiltersChange([])
    onFilteredPmids(articles.map(a => a.pmid))
  }

  return (
    <div className="border rounded-lg mb-4">
      <div 
        className="bg-gray-50 px-4 py-3 cursor-pointer flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="font-medium">Filters</span>
          {filters.length > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
              {filters.length}
            </span>
          )}
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Active filters */}
          {filters.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Active Filters:</p>
              {filters.map(filter => (
                <div key={filter.id} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded text-sm">
                  <span>
                    <strong>{filter.paramName}</strong> {filter.conditionName} <code className="bg-white px-1 rounded">{filter.valueDisplay}</code>
                  </span>
                  <button
                    onClick={() => removeFilter(filter.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Clear all filters
              </button>
            </div>
          )}

          {/* Add filter form */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Select parameter</label>
              <select
                value={selectedParam}
                onChange={(e) => {
                  setSelectedParam(e.target.value)
                  setSelectedCondition('')
                  setFilterValue('')
                }}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="">Select parameter...</option>
                {PARAM_OPTIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            {selectedParam && (
              <div>
                <label className="block text-sm font-medium mb-1">Condition</label>
                <select
                  value={selectedCondition}
                  onChange={(e) => {
                    setSelectedCondition(e.target.value)
                    setFilterValue('')
                    setFilterValueMin('')
                    setFilterValueMax('')
                  }}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">Select condition...</option>
                  {currentConditions.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedCondition && selectedParam === 'quartile' && (
              <div>
                <label className="block text-sm font-medium mb-1">Quartiles</label>
                <div className="flex gap-4">
                  {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                    <label key={q} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={selectedQuartiles.includes(q)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedQuartiles([...selectedQuartiles, q])
                          } else {
                            setSelectedQuartiles(selectedQuartiles.filter(qt => qt !== q))
                          }
                        }}
                        className="rounded"
                      />
                      {q}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {selectedCondition && selectedParam === 'topic' && availableTopics && availableTopics.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Topics {selectedCondition === 'in' ? '(Include)' : '(Exclude)'}
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
                  {availableTopics.map(topic => (
                    <label key={topic.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedTopics.includes(topic.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTopics([...selectedTopics, topic.id])
                          } else {
                            setSelectedTopics(selectedTopics.filter(t => t !== topic.id))
                          }
                        }}
                        className="rounded"
                      />
                      <span 
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold text-white"
                        style={{ 
                          backgroundColor: [
                            '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
                            '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b',
                          ][Math.abs(topic.id) % 10]
                        }}
                      >
                        {topic.name || `Topic ${topic.id + 1}`}
                      </span>
                      {topic.count !== undefined && (
                        <span className="text-xs text-gray-500">({topic.count} docs)</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {selectedCondition && selectedCondition === 'range' && selectedParam !== 'quartile' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Min</label>
                  <input
                    type="number"
                    value={filterValueMin}
                    onChange={(e) => setFilterValueMin(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max</label>
                  <input
                    type="number"
                    value={filterValueMax}
                    onChange={(e) => setFilterValueMax(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
              </div>
            )}

            {selectedCondition && selectedCondition !== 'range' && selectedParam !== 'quartile' && (
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  placeholder="Enter value..."
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
            )}

            <button
              onClick={addFilter}
              disabled={!selectedParam || !selectedCondition || 
                (selectedParam === 'quartile' && selectedQuartiles.length === 0) ||
                (selectedParam === 'topic' && selectedTopics.length === 0)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Filter
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
